import { Box, TextField } from '@mui/material';
import type { IMultilineInputData } from '@sharedInterfaces/IEmployeeTalkState';
import type { IMultilineInputComponentProperties } from '@sharedInterfaces/IEmployeeTalkTemplate';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { AppState } from '@store/store';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import type { ITemplateComponentProps } from '../TemplateEditor/types';
import ETTBaseTemplateComponent from './ETTBaseTemplateComponent';
import { useEmployeeTalk } from './ETTEmployeeTalkProvider';

export interface IETTMultilineInputComponentProps extends ITemplateComponentProps {
    // No longer need value prop - data comes from provider
}

export const ETTMultilineInputComponent: React.FC<IETTMultilineInputComponentProps> = (props) => {
    const { component } = props;
    const properties = component.properties as IMultilineInputComponentProperties;
    const placeholder = properties.placeholder || '';
    const rows = properties.rows || 4;
    const maxLength = properties.maxLength || 1000;

    // Use event-driven state management
    const { getComponentData, updateComponentData, isReadOnlyForCurrentUser } = useEmployeeTalk();
    const componentData = getComponentData<IMultilineInputData>(component.id);
    const [inputValue, setInputValue] = React.useState(componentData?.text || properties.defaultValue || '');

    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).employeeTalk;

    // Update local state when component data changes
    useEffect(() => {
        if (componentData?.text !== undefined) {
            setInputValue(componentData.text);
        }
    }, [componentData]);

    const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setInputValue(newValue);

        // Update through event system
        await updateComponentData(component.id, { text: newValue });
    };

    return (
        <ETTBaseTemplateComponent
            {...props}
            renderEditContent={() => (
                <Box>
                    <TextField
                        fullWidth
                        variant="outlined"
                        label={langStrings.placeholder}
                        value={placeholder}
                        onChange={(e) => {
                            if (props.onEdit) {
                                props.onEdit({
                                    ...component,
                                    properties: {
                                        ...properties,
                                        placeholder: e.target.value,
                                    } as IMultilineInputComponentProperties,
                                });
                            }
                        }}
                        sx={{ mb: 2 }}
                    />
                    <Box display="flex" gap={2}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            label={langStrings.rows}
                            type="number"
                            value={rows}
                            onChange={(e) => {
                                if (props.onEdit) {
                                    props.onEdit({
                                        ...component,
                                        properties: {
                                            ...properties,
                                            rows: parseInt(e.target.value) || 4,
                                        } as IMultilineInputComponentProperties,
                                    });
                                }
                            }}
                        />
                        <TextField
                            fullWidth
                            variant="outlined"
                            label={langStrings.maxLength}
                            type="number"
                            value={maxLength}
                            onChange={(e) => {
                                if (props.onEdit) {
                                    props.onEdit({
                                        ...component,
                                        properties: {
                                            ...properties,
                                            maxLength: parseInt(e.target.value) || 1000,
                                        } as IMultilineInputComponentProperties,
                                    });
                                }
                            }}
                        />
                    </Box>
                </Box>
            )}
            renderViewContent={() => (
                <TextField
                    fullWidth
                    multiline
                    rows={rows}
                    variant="outlined"
                    placeholder={placeholder}
                    inputProps={{ maxLength }}
                    value={inputValue}
                    onChange={handleChange}
                    disabled={isReadOnlyForCurrentUser}
                />
            )}
        />
    );
};

export default ETTMultilineInputComponent;
